<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Nav bar — only shown when authenticated -->
    <nav v-if="authStore.isAuthenticated" class="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
        <!-- Logo / brand -->
        <div class="flex items-center gap-6">
          <RouterLink to="/dashboard" class="flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Parent Portal
          </RouterLink>

          <!-- Nav links -->
          <div class="hidden sm:flex items-center gap-1">
            <RouterLink
              to="/dashboard"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Dashboard
            </RouterLink>
            <RouterLink
              to="/fees"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/fees' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Fees
            </RouterLink>
            <RouterLink
              to="/messages"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/messages' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Messages
            </RouterLink>
            <RouterLink
              to="/documents"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/documents' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Documents
            </RouterLink>
          </div>
        </div>

        <!-- User info + logout -->
        <div class="flex items-center gap-3">
          <span class="hidden sm:block text-sm text-gray-600 font-medium">
            {{ userDisplayName }}
          </span>
          <button
            @click="handleLogout"
            :disabled="authStore.isLoading"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>

      <!-- Mobile nav links -->
      <div class="sm:hidden flex overflow-x-auto border-t border-gray-100 px-4 py-1 gap-1 bg-gray-50">
        <RouterLink
          to="/dashboard"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Dashboard</RouterLink>
        <RouterLink
          to="/fees"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/fees' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Fees</RouterLink>
        <RouterLink
          to="/messages"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/messages' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Messages</RouterLink>
        <RouterLink
          to="/documents"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/documents' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Documents</RouterLink>
      </div>
    </nav>

    <!-- Page content -->
    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';

const authStore = useAuthStore();
const router = useRouter();

const userDisplayName = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '';
});

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}
</script>
